import React, { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import ReactConfetti from "react-confetti";
import {
  FormBaseType,
  FormBasicsDto,
  FormSectionDto,
} from "@green0meter/api-client";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCreateSubmission, useGetAvailableForms } from "@api";
import { MultistepForm, WelcomeBanner } from "@components/common";
import { Box, CircularProgress } from "@components/base";
import { useUserStore } from "@store/user";

const Form: FC = () => {
  const { formId } = useParams();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formBasics, setFormBasics] = useState<FormBasicsDto>();

  const { t } = useTranslation();

  const { user } = useUserStore();
  const {
    createSubmissionAsync,
    createSubmissionEnabled,
    isPendingCreateSubmission,
    createSubmissionError,
  } = useCreateSubmission(user);
  const { availableForms, isFetchingAvailableForms } = useGetAvailableForms(user, [
    FormBaseType.ESRS,
  ]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const handleSubmit = async (
    values: FieldValues,
    sections: FormSectionDto[],
  ): Promise<void> => {
    const isValid = sections.every((section) =>
      section.formQuestions?.every((question) => {
        if (question.isRequired) {
          return values[question.code] !== undefined && values[question.code] !== "";
        }

        return true;
      }),
    );

    if (formId && isValid && createSubmissionEnabled) {
      await createSubmissionAsync({
        formId,
      });
      setFormSubmitted(true);
    }
  };

  useEffect(() => {
    const errorData = (createSubmissionError as AxiosError)?.response?.data as {
      message: string;
    };

    if (errorData) {
      enqueueSnackbar(errorData.message, {
        variant: "error",
      });
    }
  }, [createSubmissionError]);

  useEffect(() => {
    setFormBasics(availableForms?.find((form) => form.id === formId));
  }, [availableForms]);

  return formSubmitted ? (
    <Box p={3}>
      <WelcomeBanner
        title={t("common.thankYou")}
        text={t("common.dataSubmittedAndBeingProcessed")}
        onClose={() => navigate("/csrd")}
        color="blue"
        icon="data-cloud"
      />
      <ReactConfetti recycle={false} />
    </Box>
  ) : formId && isFetchingAvailableForms ? (
    <CircularProgress />
  ) : formBasics ? (
    <MultistepForm
      formSettings={{
        formBasics,
        returnUrl: "/csrd",
        button: createSubmissionEnabled
          ? {
              label: t("form.submit", "Submit"),
              onClick: (): void => {},
              popoverButtons: [
                {
                  title: t("form.submitCalculate.title"),
                  description: t("form.submitCalculate.description"),
                  onClick: handleSubmit,
                },
                {
                  title: t("form.submitCreateReports.title"),
                  description: t("form.submitCreateReports.description"),
                  onClick: handleSubmit,
                },
              ],
            }
          : undefined,
      }}
      isSubmissionPending={isPendingCreateSubmission}
    />
  ) : (
    "Form not found"
  );
};

export default Form;
